import React from "react";

import TopNav from "../components/TopNav";
import "./AcarsPage.css";

/**
 * AcarsPage Component
 * 
 * Displays information about the FDXVA ACARS client and provides download links.
 * Features a modern, aviation-themed UI with:
 * - Hero section with animated icon
 * - Card-based layout for organized content
 * - Numbered installation steps
 * - Grid layout for additional downloads
 * - Troubleshooting tips section
 * 
 * @param {Object} props - Component props
 * @param {string} props.page - Current page identifier
 * @param {boolean} props.darkmode - Theme mode (true = dark, false = light)
 * @returns {React.ReactElement} The rendered AcarsPage component
 */
const AcarsPage = (props) => {

    const cacheBuster = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    return(
        <React.Fragment>
            <TopNav page={props.page} darkMode={props.darkmode} />
            
            <div className={`acars-container ${props.darkmode ? 'dark-theme' : 'light-theme'}`}>
                {/* Hero Header Section */}
                <header className="acars-hero" role="banner">
                    <div className="acars-hero-content">
                        {/* <span className="acars-hero-icon" role="img" aria-label="Satellite">📡</span> */}
                        <h1>FDXVA ACARS</h1>
                        <p className="acars-hero-subtitle">Our easy to use, secure, and reliable flight reporting system.</p>
                    </div>
                </header>
                
                <main className="acars-content" role="main">
                    {/* Introduction Card */}
                    <article className="acars-card" aria-labelledby="intro-heading">
                        <div className="acars-card-header">
                            <span className="acars-card-icon" role="img" aria-hidden="true">✈️</span>
                            <h2 id="intro-heading">About FDXVA ACARS</h2>
                        </div>
                        <div className="acars-card-content">
                            <p>
                                Flight reports are submitted via our custom and secure ACARS system. 
                                We have processed more than 68,000 flight reports.
                                Use the links below to download the ACARS client.
                            </p>
                            
                            <div className="acars-warning-box" role="alert">
                                <span className="acars-warning-icon" role="img" aria-hidden="true">⚠️</span>
                                <div className="acars-warning-content">
                                    <h4>You are the installer!</h4>
                                    <p>This is a portable application that requires manual setup. Please follow the installation steps carefully.</p>
                                </div>
                            </div>
                        </div>
                    </article>
                    
                    {/* Installation Instructions Card */}
                    <article className="acars-card" aria-labelledby="install-heading">
                        <div className="acars-card-header">
                            <span className="acars-card-icon" role="img" aria-hidden="true">📋</span>
                            <h2 id="install-heading">Installation Steps</h2>
                        </div>
                        <div className="acars-card-content">
                            <div className="acars-steps" role="list">
                                <li className="acars-step">
                                    <div className="acars-step-content">
                                        Download the zip file from the link below. 
                                        (<em>right-click and "save link as..."</em> - ignore any message about secure download, suspicious file, etc.)
                                    </div>
                                </li>
                                <li className="acars-step">
                                    <div className="acars-step-content">
                                        Create a new folder where you want to put the client.
                                    </div>
                                </li>
                                <li className="acars-step">
                                    <div className="acars-step-content">
                                        Extract the files from the downloaded zip to that folder.
                                    </div>
                                </li>
                                <li className="acars-step">
                                    <div className="acars-step-content">
                                        You may need to <em>Run As Admin</em>, disable antivirus or other programs.
                                    </div>
                                </li>
                                <li className="acars-step">
                                    <div className="acars-step-content">
                                        After starting the ACARS client, click on the <em>Settings</em> tab and enter your FDXVA callsign and password (the same password you use to login to the website).
                                    </div>
                                </li>
                            </div>
                            
                            <div className="acars-download-section">
                                <a 
                                    alt="Download ACARS" 
                                    key="b25e02de" 
                                    href={`https://fdxva.org/downloads/fdxva_acars.zip?x=${cacheBuster}`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="acars-download-btn"
                                    aria-label="Download FDXVA ACARS client"
                                >
                                    <span className="acars-download-icon" role="img" aria-hidden="true">⬇️</span>
                                    DOWNLOAD FDXVA ACARS
                                </a>
                                <p className="acars-download-note">
                                    Right-click and "Save link as..." to download
                                </p>
                            </div>
                        </div>
                    </article>
                    
                    {/* Troubleshooting Card */}
                    <article className="acars-card" aria-labelledby="troubleshoot-heading">
                        <div className="acars-card-header">
                            <span className="acars-card-icon" role="img" aria-hidden="true">🔧</span>
                            <h2 id="troubleshoot-heading">Troubleshooting</h2>
                        </div>
                        <div className="acars-card-content">
                            <div className="acars-troubleshooting-list" role="list">
                                <li className="acars-troubleshooting-item">
                                    <span className="acars-troubleshooting-icon" role="img" aria-hidden="true">✓</span>
                                    <span className="acars-troubleshooting-text">
                                        You can safely ignore any browser messages that the download was blocked - this is a known false positive and users have successfully logged more than 50,000 flights with this client. In Chrome select <code>Keep</code> rather than <code>Discard</code>.
                                    </span>
                                </li>
                                <li className="acars-troubleshooting-item">
                                    <span className="acars-troubleshooting-icon" role="img" aria-hidden="true">✓</span>
                                    <span className="acars-troubleshooting-text">
                                        Some users report the need to <code>Run as Administrator</code>.
                                    </span>
                                </li>
                                <li className="acars-troubleshooting-item">
                                    <span className="acars-troubleshooting-icon" role="img" aria-hidden="true">✓</span>
                                    <span className="acars-troubleshooting-text">
                                        Antivirus software may need to be temporarily disabled, and/or set to ignore <code>fsacars.exe</code>
                                    </span>
                                </li>
                                <li className="acars-troubleshooting-item">
                                    <span className="acars-troubleshooting-icon" role="img" aria-hidden="true">✓</span>
                                    <span className="acars-troubleshooting-text">
                                        Web blocking software (e.g., Trend Micro) users MUST add <code>fdxva.org</code> to the exception list.
                                    </span>
                                </li>
                            </div>
                        </div>
                    </article>
                    
                    {/* Additional Downloads Card */}
                    <article className="acars-card" aria-labelledby="additional-heading">
                        <div className="acars-card-header">
                            <span className="acars-card-icon" role="img" aria-hidden="true">📦</span>
                            <h2 id="additional-heading">Additional Files</h2>
                        </div>
                        <div className="acars-card-content">
                            <div className="acars-info-box">
                                <div className="acars-info-box-header">
                                    <span role="img" aria-hidden="true">💡</span>
                                    <span>Download Note</span>
                                </div>
                                <p>You may already have these additional files. The ACARS client requires FSUIPC7 (MSFS) or XPUIPC (X-Plane) to be installed.</p>
                            </div>
                            
                            <nav className="acars-additional-grid" aria-label="Additional downloads">
                                {/* X-Plane */}
                                <div className="acars-addon-card">
                                    <div className="acars-addon-header">
                                        <span className="acars-addon-icon" role="img" aria-hidden="true">🛫</span>
                                        <span className="acars-addon-title">X-Plane</span>
                                    </div>
                                    <p className="acars-addon-description">
                                        XPUIPC 2.0.3.5 and XPWideClient 2.0.0.3 for X-Plane simulator integration.
                                    </p>
                                    <a 
                                        href='downloads/XPUIPC_2.0.3.5_and_XPWideClient_2.0.0.3.zip' 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="acars-addon-link"
                                        aria-label="Download XPUIPC 2.0.3.5 for X-Plane"
                                    >
                                        <span role="img" aria-hidden="true">⬇️</span>
                                        Download XPUIPC 2.0.3.5
                                    </a>
                                </div>
                                
                                {/* MSFS 2020-2021 */}
                                <div className="acars-addon-card">
                                    <div className="acars-addon-header">
                                        <span className="acars-addon-icon" role="img" aria-hidden="true">🎮</span>
                                        <span className="acars-addon-title">MSFS 2020-2021</span>
                                    </div>
                                    <p className="acars-addon-description">
                                        FSUIPC 7 for Microsoft Flight Simulator 2020 and 2024 integration.
                                    </p>
                                    <a 
                                        href='https://fsuipc.simflight.com/beta/Install_FSUIPC7.zip' 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="acars-addon-link"
                                        aria-label="Download FSUIPC 7 for MSFS"
                                    >
                                        <span role="img" aria-hidden="true">⬇️</span>
                                        Download FSUIPC 7
                                    </a>
                                </div>
                                
                                {/* FSX/P3D */}
                                <div className="acars-addon-card">
                                    <div className="acars-addon-header">
                                        <span className="acars-addon-icon" role="img" aria-hidden="true">✈️</span>
                                        <span className="acars-addon-title">FSX/P3D</span>
                                    </div>
                                    <p className="acars-addon-description">
                                        FSUIPC 6 for FSX and Prepar3D simulator integration.
                                    </p>
                                    <a 
                                        href='http://www.fsuipc.com/' 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="acars-addon-link"
                                        aria-label="Download FSUIPC 6 for FSX and Prepar3D"
                                    >
                                        <span role="img" aria-hidden="true">⬇️</span>
                                        Download FSUIPC 6
                                    </a>
                                </div>
                            </nav>
                        </div>
                    </article>
                </main>
            </div>
        </React.Fragment>
    );
}

export default AcarsPage;
